import { ActivityCategory } from "./types";
import {
  SparklesIcon,
  MovieIcon,
  DiningIcon,
  FitnessIcon,
  SunIcon,
  UsersIcon,
  GamepadIcon,
  CoffeeIcon,
} from "./components/icons/FeatherIcons";

// Feature Flags
export const FEATURE_FLAGS = {
  storyFeature: false, // Enable/disable stories functionality
  starRating: false, // Enable/disable star rating display
};

// Chat reactions (shared defaults/options)
export const CHAT_REACTION_QUICK_DEFAULT = [
  "👍",
  "❤️",
  "😂",
  "😮",
  "😢",
  "🙏",
];

export const CHAT_REACTION_ALL_OPTIONS = [
  ...CHAT_REACTION_QUICK_DEFAULT,
  "🎉",
  "💯",
  "😃",
  "😞",
  "😡",
  "🤬",
  "👿",
  "💢",
  "👎",
  "👌",
];

export const CATEGORIES = [
  { name: "Movies", value: ActivityCategory.Movies, Icon: MovieIcon },
  { name: "Dining", value: ActivityCategory.Dining, Icon: DiningIcon },
  { name: "Games", value: ActivityCategory.Games, Icon: GamepadIcon },
  { name: "Outdoor", value: ActivityCategory.Outdoor, Icon: SunIcon },
  { name: "Drinks", value: ActivityCategory.Drinks, Icon: CoffeeIcon },
  { name: "Fitness", value: ActivityCategory.Fitness, Icon: FitnessIcon },
  { name: "General", value: ActivityCategory.General, Icon: UsersIcon },
];

// Categories with "All" filter for UI filtering purposes only
export const FILTER_CATEGORIES = [
  { name: "All", value: "all", Icon: SparklesIcon },
  ...CATEGORIES,
];

// Valid categories for activity creation (excludes 'all' filter)
export const VALID_ACTIVITY_CATEGORIES = CATEGORIES;

// Validation function to ensure category is valid
export const validateActivityCategory = (
  category: string,
): category is ActivityCategory => {
  // Normalize the category (trim whitespace, lowercase)
  const normalized = category?.trim().toLowerCase();
  return Object.values(ActivityCategory).includes(
    normalized as ActivityCategory,
  );
};

// Get default category if invalid category is provided
export const getValidCategory = (
  category: string | undefined,
): ActivityCategory => {
  if (category) {
    // Remove emojis and normalize the category
    const cleanCategory = category
      .replace(
        /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu,
        "",
      ) // Remove emojis
      .trim()
      .toLowerCase();

    // Try direct match first
    if (validateActivityCategory(cleanCategory)) {
      return cleanCategory as ActivityCategory;
    }

    // Try mapping common variations
    const categoryMappings: Record<string, ActivityCategory> = {
      outdoor: ActivityCategory.Outdoor,
      outdoors: ActivityCategory.Outdoor,
      movie: ActivityCategory.Movies,
      movies: ActivityCategory.Movies,
      film: ActivityCategory.Movies,
      dining: ActivityCategory.Dining,
      food: ActivityCategory.Dining,
      restaurant: ActivityCategory.Dining,
      game: ActivityCategory.Games,
      games: ActivityCategory.Games,
      gaming: ActivityCategory.Games,
      drink: ActivityCategory.Drinks,
      drinks: ActivityCategory.Drinks,
      coffee: ActivityCategory.Drinks,
      fitness: ActivityCategory.Fitness,
      workout: ActivityCategory.Fitness,
      exercise: ActivityCategory.Fitness,
    };

    if (categoryMappings[cleanCategory]) {
      return categoryMappings[cleanCategory];
    }
  }
  return ActivityCategory.General; // Default to General for unmatched activities
};

export const PASSIONS = [
  "Festivals",
  "Travel",
  "Plant-based",
  "Movies",
  "Road Trips",
  "Books",
  "Writer",
  "Fashion",
  "Stand up Comedy",
  "Coffee",
  "Chatting",
  "Cycling",
  "DIY",
  "Dog lover",
  "House Parties",
  "Swimming",
  "Athlete",
  "Bollywood",
  "Hollywood",
  "Cricket",
  "Trivia",
  "Tea Break",
  "Environmentalism",
  "Yoga",
  "Running",
  "Music",
  "Astrology",
  "Spirituality",
  "Art",
  "Photography",
  "Baking",
  "Volunteering",
  "Podcasts",
  "Startups",
  "History",
  "Politics",
];

export const MOCK_STORY_IMAGES = [
  "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=600&fit=crop", // Nature/Outdoor
  "https://images.unsplash.com/photo-1514565131-fce0801e5785?w=400&h=600&fit=crop", // City/Urban
  "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400&h=600&fit=crop", // Food/Dining
  "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=600&fit=crop", // Music
  "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400&h=600&fit=crop", // Travel
  "https://images.unsplash.com/photo-1536924940846-227afb31e2a5?w=400&h=600&fit=crop", // Art
  "https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=400&h=600&fit=crop", // People/Social
  "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=400&h=600&fit=crop", // Tech
  "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=600&fit=crop", // Fitness
  "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400&h=600&fit=crop", // Events
  "https://images.unsplash.com/photo-1489599397616-73c5c01d0e37?w=400&h=600&fit=crop", // Movies
  "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=600&fit=crop", // Books
];
